import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { dashApi } from "@/lib/dashApi";
import { useAuth } from "@/lib/auth";
import { fmtPct, fmtRelative, fmtValue } from "@/lib/format";
import {
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  Panel,
  SourceBadge,
  Stat,
  StatusPill,
} from "@/components/dash/ui";

export default function ExecutiveReportsPage() {
  const auth = useAuth();
  const qc = useQueryClient();
  const isAdmin = (auth.user?.roles ?? []).includes("admin");
  const [days, setDays] = useState(30);
  const report = useQuery({ queryKey: ["report", days], queryFn: () => dashApi.report(days) });
  const status = useQuery({ queryKey: ["wh-status"], queryFn: dashApi.warehouseStatus });

  const backfill = useMutation({
    mutationFn: () => dashApi.warehouseBackfill(90),
    onSuccess: () => qc.invalidateQueries(),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title="Executive Reports"
        description="Board-ready management summary from the historical KPI warehouse."
        right={
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} className="rounded-lg border border-ink-500/60 bg-ink-800 px-2.5 py-1.5 text-xs text-slate-200 [color-scheme:dark]">
            {[7, 30, 90].map((d) => <option key={d} value={d}>last {d} days</option>)}
          </select>
        }
      />

      {status.data && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
          <Stat label="History from" value={<span className="text-sm">{status.data.earliest ?? "—"}</span>} />
          <Stat label="History to" value={<span className="text-sm">{status.data.latest ?? "—"}</span>} />
          <Stat label="KPI rows" value={status.data.rows.toLocaleString()} />
          <Stat label="Distinct KPIs" value={status.data.distinct_kpis} />
          <Stat label="Leader rows" value={status.data.leader_rows} />
        </div>
      )}
      {isAdmin && (
        <button
          type="button"
          onClick={() => backfill.mutate()}
          disabled={backfill.isPending}
          className="rounded-lg border border-ink-500/60 bg-ink-800 px-3 py-1.5 text-xs text-slate-200 hover:bg-ink-700 disabled:opacity-60"
        >
          {backfill.isPending ? "Backfilling…" : backfill.isSuccess ? "Backfilled ✓" : "Backfill 90 days"}
        </button>
      )}

      {report.isLoading ? <LoadingState /> : report.isError ? <ErrorState error={report.error} /> : report.data ? (
        <>
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
            {report.data.sections.map((s) => (
              <Panel key={s.key} title={`${s.title} · last ${report.data!.period_days}d`}>
                {!s.has_data ? (
                  <EmptyState compact>No stored history yet.</EmptyState>
                ) : (
                  <table className="w-full text-sm">
                    <tbody className="divide-y divide-ink-500/40">
                      {s.metrics.map((m) => {
                        const up = (m.change_pct ?? 0) >= 0;
                        return (
                          <tr key={m.kpi} className="text-slate-300">
                            <td className="py-2">{m.label}</td>
                            <td className="py-2 text-right tabular-nums text-white">{fmtValue(m.current, m.unit)}</td>
                            <td className={`py-2 pl-4 text-right text-xs ${m.change_pct === null ? "text-slate-600" : up ? "text-emerald-400" : "text-rose-400"}`}>
                              {m.change_pct === null ? "—" : `${up ? "▲" : "▼"} ${fmtPct(m.change_pct)}`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </Panel>
            ))}
          </div>

          <Panel title={`Operational risks (${report.data.risk_counts.critical ?? 0} critical, ${report.data.risk_counts.warning ?? 0} warning)`}>
            {!report.data.risks.length ? (
              <EmptyState compact>No open operational risks.</EmptyState>
            ) : (
              <ul className="divide-y divide-ink-500/40">
                {report.data.risks.map((r, i) => (
                  <li key={i} className="flex items-start gap-3 py-2.5">
                    <StatusPill status={r.severity} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-slate-100">{r.title}</p>
                        {r.source && <SourceBadge code={r.source} />}
                      </div>
                      {r.detail && <p className="text-xs text-slate-400">{r.detail}</p>}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </Panel>
          <p className="text-xs text-slate-600">{report.data.note}</p>
        </>
      ) : null}

      {status.data && (
        <p className="text-[11px] text-slate-600">
          Warehouse updated {status.data.latest ? fmtRelative(status.data.latest + "T00:00:00Z") : "never"}.
        </p>
      )}
    </div>
  );
}
